import "reflect-metadata";
import {
  container as tsyringeContainer,
  type DependencyContainer,
  type InjectionToken,
} from "tsyringe";

/**
 * IOC — dependency resolution seam for the web port.
 *
 * Mirrors the Unity-side `IOC.Resolve<T>()` pattern (see
 * `shared-application-code/Shared/Assets/Campfire/Runtime/Infrastructure/IOC.cs`
 * and callers across `Models/*` and `ViewModels/*`). All long-lived app services
 * (ProjectModel, SceneModel, AuthModel, AI*Model, etc.) MUST be registered here
 * and resolved through this container instead of imported as singletons.
 *
 * Keep the public API small (register / resolve) so the container stays easy to
 * swap if we migrate off tsyringe later.
 */

export type IocToken<T> = InjectionToken<T>;

export class Ioc {
  private static m_container: DependencyContainer = tsyringeContainer;

  static registerSingleton<T>(token: IocToken<T>, ctor: new (...args: unknown[]) => T): void {
    this.m_container.registerSingleton(token as never, ctor as never);
  }

  static registerInstance<T>(token: IocToken<T>, instance: T): void {
    this.m_container.registerInstance(token as never, instance as never);
  }

  static resolve<T>(token: IocToken<T>): T {
    return this.m_container.resolve<T>(token as never);
  }

  static isRegistered<T>(token: IocToken<T>): boolean {
    return this.m_container.isRegistered(token as never);
  }

  static reset(): void {
    this.m_container.reset();
  }
}
